import { COL } from "../api/sheets";
import { Row } from "../types/sheets";

// Imported/placeholder rows often carry this literal "not set" value instead of a blank cell;
// for task detection it must count the same as empty.
export const MISSING_PLACEHOLDER = "未設置";

/** A cell counts as "missing" when it is empty/whitespace or the literal 未設置 placeholder. */
export function isMissing(v: string | number | null | undefined): boolean {
  const s = String(v ?? "").trim();
  return s === "" || s === MISSING_PLACEHOLDER;
}

export type TaskKey =
  | "missingId"
  | "missingLocation"
  | "missingCoords"
  | "missingInstallSpot";

// Most fundamental first: identity → administrative placement → map position → install spot.
export const TASK_KEYS: TaskKey[] = [
  "missingId",
  "missingLocation",
  "missingCoords",
  "missingInstallSpot",
];

export interface TaskRow {
  row: Row;
  /** Index of the row within the array passed to analyzeTasks (matches readAllRows order). */
  idx: number;
}

export interface TaskGroup {
  key: TaskKey;
  rows: TaskRow[];
}

// 設置場所 is split out from the location group on purpose: it is high-volume and has a lighter,
// distinct workflow (a fieldworker can usually infer it from the ID + their documentation).
const LOCATION_COLS = ["キャンパス", "部局、施設名", "建物", "階"];

function matches(key: TaskKey, r: Row): boolean {
  switch (key) {
    case "missingId":
      return isMissing(r[COL["ID"]]);
    case "missingLocation":
      return LOCATION_COLS.some((c) => isMissing(r[COL[c]]));
    case "missingCoords":
      return isMissing(r[COL["緯度"]]) || isMissing(r[COL["経度"]]);
    case "missingInstallSpot":
      return isMissing(r[COL["設置場所"]]);
    default:
      return false;
  }
}

/**
 * Buckets rows into the four task types. A row appears under every type it matches (a record
 * missing both its ID and its coordinates shows up in both groups).
 */
export function analyzeTasks(rows: Row[]): TaskGroup[] {
  const indexed = rows.map((row, idx) => ({ row, idx }));
  return TASK_KEYS.map((key) => ({
    key,
    rows: indexed.filter(({ row }) => matches(key, row)),
  }));
}
